import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/hooks/useAuth';
import React, { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { getMostRecentUnreadNotification } from '@/utils/notification-navigation';
import { showNotificationToast } from '@/utils/notification-toast';
// Export the hook from the new file
export { useToastNotificationSettings } from './useToastNotificationSettings';

/**
 * Toast Notification Provider
 * Handles showing toast notifications for new notifications with actionable links.
 *
 * Dedupes by notification ID via the shared {@link showNotificationToast} helper,
 * so StrictMode double-invocations and concurrent callers (realtime INSERT +
 * array fallback) cannot stack the same notification twice.
 */
export const ToastNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { notifications } = useNotifications();
  const navigate = useNavigate();

  // Fallback: if a recent unread notification is already in the list when the
  // provider mounts, toast it once. Realtime INSERT events are handled in
  // useRealtimeNotifications via the same showNotificationToast helper.
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !notifications.length) {
      return;
    }

    const recentNotification = getMostRecentUnreadNotification(notifications);
    if (recentNotification) {
      showNotificationToast(recentNotification, navigate);
    }
  }, [notifications, isAuthenticated, user?.id, navigate]);

  return <>{children}</>;
};
